import React, { useEffect, useRef } from 'react';
import { FaCopy, FaTrash, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import  { logger } from '../../../../utils/logger';

const RowActionPopup = ({
  row,
  position,
  onClose,
  onEdit,
  onDuplicate,
  onDelete,
  tableName,
  themeColor = 'emerald'
}) => {
  const navigate = useNavigate();
  const popupRef = useRef(null);

  // Chiudi il popup quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Aggiungi listener per click e context menu
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside);
    
    // Chiudi anche con ESC
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const handleDuplicate = () => {
    onDuplicate(row);
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm(`Sei sicuro di voler eliminare questo record?`)) {
      onDelete(row);
      onClose();
    }
  };

  const handleNavigateToTabelle = () => {
    let tableId = null;
    
    if (tableName === 'SKE_MacroFunzioni') {
      tableId = row.MAC_Id_Tabelle;
    } else if (tableName === 'SKE_Oggetti') {
      tableId = row.OGG_Id_Tabelle;
    } else if (tableName === 'SKE_Campi') {
      tableId = row.CAM_Id_Tabelle;
    }
    
    if (!tableId) {
      logger.warn(`[RowActionPopup] Non è possibile determinare l'ID della tabella per la navigazione. TableName: ${tableName}, Row:`, row);
      return;
    }

    logger.info(`[RowActionPopup] Navigating to Tabelle page with filter TAB_Id_Tabelle: ${tableId}`);
    
    navigate('/sisketron/tabelle', {
      state: {
        presetFilter: {
          TAB_Id_Tabelle: tableId
        }
      }
    });
    
    onClose();
  };

  // ✅ CORREZIONE: Nuovo approccio per navigazione in nuova scheda
  const handleNavigateToTabelleNewTab = () => {
    let tableId = null;
    
    if (tableName === 'SKE_MacroFunzioni') {
      tableId = row.MAC_Id_Tabelle;
    } else if (tableName === 'SKE_Oggetti') {
      tableId = row.OGG_Id_Tabelle;
    } else if (tableName === 'SKE_Campi') {
      tableId = row.CAM_Id_Tabelle;
    }
    
    if (!tableId) {
      logger.warn(`[RowActionPopup] Non è possibile determinare l'ID della tabella per la navigazione. TableName: ${tableName}, Row:`, row);
      return;
    }

    logger.info(`[RowActionPopup] Opening Tabelle page in new tab with filter TAB_Id_Tabelle: ${tableId}`);
    
    // ✅ MIGLIORATO: Usa localStorage invece di sessionStorage per maggiore affidabilità
    const filterData = {
      presetFilter: {
        TAB_Id_Tabelle: tableId
      }
    };
    const storageKey = `tabelle_filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Usa localStorage per maggiore affidabilità
    localStorage.setItem(storageKey, JSON.stringify(filterData));
    
    // ✅ MIGLIORATO: Apri la nuova scheda con URL completo
    const url = `/sisketron/tabelle?filterKey=${storageKey}`;
    window.open(url, '_blank');
    
    onClose();
  };

  const handleNavigateToOggetti = () => {
    let oggettoId = null;
    
    if (tableName === 'SKE_Attributi') {
      oggettoId = row.ATT_Id_Oggetti;
    }
    
    if (!oggettoId) {
      logger.warn(`[RowActionPopup] Non è possibile determinare l'ID dell'oggetto per la navigazione. TableName: ${tableName}, Row:`, row);
      return;
    }

    logger.info(`[RowActionPopup] Navigating to Oggetti page with filter OGG_Id_Oggetti: ${oggettoId}`);
    
    navigate('/sisketron/oggetti', {
      state: {
        presetFilter: {
          OGG_Id_Oggetti: oggettoId
        }
      }
    });
    
    onClose();
  };

  // ✅ CORREZIONE: Navigazione verso Oggetti in nuova scheda
  const handleNavigateToOggettiNewTab = () => {
    let oggettoId = null;
    
    if (tableName === 'SKE_Attributi') {
      oggettoId = row.ATT_Id_Oggetti;
    }
    
    if (!oggettoId) {
      logger.warn(`[RowActionPopup] Non è possibile determinare l'ID dell'oggetto per la navigazione. TableName: ${tableName}, Row:`, row);
      return;
    }

    logger.info(`[RowActionPopup] Opening Oggetti page in new tab with filter OGG_Id_Oggetti: ${oggettoId}`);
    
    const filterData = {
      presetFilter: {
        OGG_Id_Oggetti: oggettoId
      }
    };
    const storageKey = `oggetti_filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Usa localStorage per maggiore affidabilità
    localStorage.setItem(storageKey, JSON.stringify(filterData));
    
    const url = `/sisketron/oggetti?filterKey=${storageKey}`;
    window.open(url, '_blank');
    
    onClose();
  };

  const handleNavigateToCampi = () => {
    let tabellaId = null;
    
    if (tableName === 'SKE_Tabelle') {
      tabellaId = row.TAB_Id_Tabelle;
    }
    
    if (!tabellaId) {
      logger.warn(`[RowActionPopup] Non è possibile determinare l'ID della tabella per la navigazione verso Campi. TableName: ${tableName}, Row:`, row);
      return;
    }

    logger.info(`[RowActionPopup] Navigating to Campi page with filter CAM_Id_Tabelle: ${tabellaId}`);
    
    navigate('/sisketron/campi', {
      state: {
        presetFilter: {
          CAM_Id_Tabelle: tabellaId
        }
      }
    });
    
    onClose();
  };

  // ✅ CORREZIONE: Navigazione verso Campi in nuova scheda
  const handleNavigateToCampiNewTab = () => {
    let tabellaId = null;
    
    if (tableName === 'SKE_Tabelle') {
      tabellaId = row.TAB_Id_Tabelle;
    }
    
    if (!tabellaId) {
      logger.warn(`[RowActionPopup] Non è possibile determinare l'ID della tabella per la navigazione verso Campi. TableName: ${tableName}, Row:`, row);
      return;
    }

    logger.info(`[RowActionPopup] Opening Campi page in new tab with filter CAM_Id_Tabelle: ${tabellaId}`);
    
    const filterData = {
      presetFilter: {
        CAM_Id_Tabelle: tabellaId
      }
    };
    const storageKey = `campi_filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Usa localStorage per maggiore affidabilità
    localStorage.setItem(storageKey, JSON.stringify(filterData));
    
    const url = `/sisketron/campi?filterKey=${storageKey}`;
    window.open(url, '_blank');
    
    onClose();
  };

  // ✅ NUOVO: Navigazione verso Attributi
  const handleNavigateToAttributi = () => {
    let oggettoId = null;
    
    if (tableName === 'SKE_Oggetti') {
      oggettoId = row.OGG_Id_Oggetti;
    }
    
    if (!oggettoId) {
      logger.warn(`[RowActionPopup] Nessun OGG_Id_Oggetti trovato per la navigazione agli attributi.`);
      return;
    }

    logger.info(`[RowActionPopup] Navigating to Attributi page with filter ATT_Id_Oggetti: ${oggettoId}`);
    
    navigate('/sisketron/attributi', {
      state: {
        presetFilter: {
          ATT_Id_Oggetti: oggettoId
        }
      }
    });
    
    onClose();
  };

  // ✅ NUOVO: Navigazione verso Attributi in nuova scheda
  const handleNavigateToAttributiNewTab = () => {
    let oggettoId = null;
    
    if (tableName === 'SKE_Oggetti') {
      oggettoId = row.OGG_Id_Oggetti;
    }
    
    if (!oggettoId) {
      logger.warn(`[RowActionPopup] Nessun OGG_Id_Oggetti trovato per la navigazione agli attributi.`);
      return;
    }

    logger.info(`[RowActionPopup] Opening Attributi page in new tab with filter ATT_Id_Oggetti: ${oggettoId}`);
    
    const filterData = {
      presetFilter: {
        ATT_Id_Oggetti: oggettoId
      }
    };
    const storageKey = `attributi_filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    localStorage.setItem(storageKey, JSON.stringify(filterData));
    
    const url = `/sisketron/attributi?filterKey=${storageKey}`;
    window.open(url, '_blank');
    
    onClose();
  };

  const shouldShowTableNavigation = () => {
    if (tableName === 'SKE_MacroFunzioni' && row.MAC_Id_Tabelle) {
      return true;
    } else if (tableName === 'SKE_Oggetti' && row.OGG_Id_Tabelle) {
      return true;  
    } else if (tableName === 'SKE_Campi' && row.CAM_Id_Tabelle) {
      return true;
    }
    
    return false;
  };

  const shouldShowOggettiNavigation = () => {
    if (tableName === 'SKE_Attributi' && row.ATT_Id_Oggetti) {
      return true;
    }
    
    return false;
  };

  const shouldShowCampiNavigation = () => {
    if (tableName === 'SKE_Tabelle' && row.TAB_Id_Tabelle) {
      return true;
    }
    
    return false;
  };

  const shouldShowAttributiNavigation = () => {
    return tableName === 'SKE_Oggetti' && row.OGG_Id_Oggetti;
  };

  const popupWidth = 200; // Increased width for longer text
  const baseHeight = 90;
  const extraHeight = (shouldShowTableNavigation() ? 60 : 0) + // 60 for both buttons
                     (shouldShowOggettiNavigation() ? 60 : 0) + 
                     (shouldShowCampiNavigation() ? 60 : 0) +
                     (shouldShowAttributiNavigation() ? 60 : 0); // ✅ NUOVO
  const popupHeight = baseHeight + extraHeight;

  const popupStyle = {
    top: position.mouseY ? position.mouseY + 10 : position.viewportTop + 10,
    left: position.mouseX ? position.mouseX - 50 : position.left - 50,
    zIndex: 1000,
    position: 'fixed',
  };

  const mouseY = position.mouseY || position.viewportTop;
  const mouseX = position.mouseX || position.left;
  
  if (mouseY + popupHeight + 20 > window.innerHeight) {
    popupStyle.top = mouseY - popupHeight - 10;
  }

  if (mouseX + popupWidth > window.innerWidth) {
    popupStyle.left = window.innerWidth - popupWidth - 10;
  }

  if (popupStyle.left < 10) {
    popupStyle.left = 10;
  }

  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600"
      style={popupStyle}
    >
      <div className="p-2 space-y-1">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Azioni</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <FaTimes size={10} />
          </button>
        </div>
        
        <button
          onClick={handleDuplicate}
          className={`w-full flex items-center px-3 py-2 text-sm text-${themeColor}-700 dark:text-${themeColor}-300 hover:bg-${themeColor}-50 dark:hover:bg-${themeColor}-900/20 rounded transition-colors text-left`}
        >
          <FaCopy className="mr-2 flex-shrink-0" size={12} />
          <span>Duplica</span>
        </button>
        
        <button
          onClick={handleDelete}
          className="w-full flex items-center px-3 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors text-left"
        >
          <FaTrash className="mr-2 flex-shrink-0" size={12} />
          <span>Elimina</span>
        </button>

        {/* Navigation Buttons */}
        {shouldShowTableNavigation() && (
          <>
            <button
              onClick={handleNavigateToTabelle}
              className={`w-full flex items-center px-3 py-2 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai alla tabella</span>
            </button>
           
          </>
        )}

        {shouldShowOggettiNavigation() && (
          <>
            <button
              onClick={handleNavigateToOggetti}
              className={`w-full flex items-center px-3 py-2 text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai all'Oggetto</span>
            </button>
            <button
              onClick={handleNavigateToOggettiNewTab}
              className={`w-full flex items-center px-3 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai all'Oggetto (nuova scheda)</span>
            </button>
          </>
        )}

        {shouldShowCampiNavigation() && (
          <>
            <button
              onClick={handleNavigateToCampi}
              className={`w-full flex items-center px-3 py-2 text-sm text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai ai Campi</span>
            </button>
            <button
              onClick={handleNavigateToCampiNewTab}
              className={`w-full flex items-center px-3 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai ai Campi (nuova scheda)</span>
            </button>
          </>
        )}

        {/* ✅ NUOVO: Navigazione verso Attributi */}
        {shouldShowAttributiNavigation() && (
          <>
            <button
              onClick={handleNavigateToAttributi}
              className={`w-full flex items-center px-3 py-2 text-sm text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai agli Attributi</span>
            </button>
            <button
              onClick={handleNavigateToAttributiNewTab}
              className={`w-full flex items-center px-3 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors text-left`}
            >
              <FaExternalLinkAlt className="mr-2 flex-shrink-0" size={12} />
              <span>Vai agli Attributi (nuova scheda)</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RowActionPopup;